import { getCustomButtonTemplate } from "../../shortcodes/templates";
import { addShortcodeToEditor, getShortcodeWrapper } from "@/assets/utilities/tinymce/shortodeAddition";
import { parseToHtml } from "@/utility/parseToHtml";
import { CUSTOM_BUTTON_EVENT_TYPES } from "@/constant";

const DATA_ATTR = "data-custom-button-data";
const DIALOG_URL = "/custom-button-dialog";
const DIALOG_TITLE = "Custom Button";
const DIALOG_SIZE = { width: 1200, height: 700 };
const IMAGES_API = process.env.VUE_APP_API;

const isRelativeAssetUrl = value =>
  !!value && typeof value === "string" && !/^(data:|blob:|https?:\/\/)/i.test(value);

const toAbsoluteAssetUrl = value => {
  if (!isRelativeAssetUrl(value)) return value;
  if (!IMAGES_API) return value;
  try {
    return new URL(value, IMAGES_API).toString();
  } catch (e) {
    return value;
  }
};

const dialogButtons = [
  { type: "cancel", name: "close", text: "Cancel" },
  { type: "custom", name: "submit", text: "Submit", primary: true },
];

const CUSTOM_BUTTON_ICON_SVG = {
  check: `<svg viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill="currentColor" d="M17.7316 33.0767C16.8254 32.5574 15.8963 32.2377 15.2519 31.6191C12.6714 29.1375 10.1521 26.5889 7.66411 24.0133C6.1769 22.4731 5.7187 21.303 7.00013 20C8.2109 18.7691 9.52255 19.0455 10.9994 20.5C12.5448 22.0224 15.0929 24.8357 16.5593 26.4323C17.4282 27.3779 18.0091 27.2933 18.8717 26.4135C23.2159 21.9841 28.581 16.3552 33 12C33.5695 11.4389 33.7247 11.1515 34.5 11C35.7949 10.7471 36.919 10.7597 37.5 12C38.0623 13.1996 37.6079 13.9906 36.6413 14.9749C33.3198 18.3541 29.9972 21.7333 26.658 25.0968C24.4204 27.3507 22.1818 29.6035 19.886 31.7957C19.3415 32.3161 18.5662 32.5929 17.7306 33.0778L17.7316 33.0767Z"/></svg>`,
  arrow: `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill="currentColor" d="M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20zm-.6 5.2c-.2 0-.4.1-.5.3a.7.7 0 0 0 0 .9l2.3 2.3H7.3c-.4 0-.6.2-.6.6s.2.6.6.6h6l-2.3 2.3c-.2.2-.3.4-.3.6s.1.4.3.6c.3.2.7.2 1 0l3.7-3.8-3.7-3.8c-.3-.2-.7-.2-1 0-.1-.1-.3-.2-.5-.2z"/></svg>`,
  phone: `<svg viewBox="0 0 46 44" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill="currentColor" d="M30.3887 22.0919H35.3887C35.3887 18.9818 34.0717 15.9991 31.7275 13.7998C29.3833 11.6006 26.2039 10.3651 22.8887 10.3651V15.0559C24.8778 15.0559 26.7855 15.7972 28.192 17.1167C29.5985 18.4362 30.3887 20.2259 30.3887 22.0919ZM40.3887 22.0919H45.3887C45.3887 10.3651 35.3137 0.983704 22.8887 0.983704V5.67443C32.5387 5.67443 40.3887 13.0154 40.3887 22.0919ZM42.8887 30.3007C39.7637 30.3007 36.7637 29.8316 33.9637 28.9639C33.0887 28.7059 32.1137 28.8935 31.4137 29.5502L25.9137 34.71C18.822 31.3169 13.0554 25.9071 9.43867 19.2541L14.9387 14.0943C15.6387 13.4376 15.8387 12.5229 15.5637 11.702C14.6153 9.00028 14.1342 6.17351 14.1387 3.32906C14.1387 2.70704 13.8753 2.11048 13.4064 1.67064C12.9376 1.2308 12.3017 0.983704 11.6387 0.983704H2.88867C2.22563 0.983704 1.58975 1.2308 1.1209 1.67064C0.652064 2.11048 0.388672 2.70704 0.388672 3.32906C0.388672 13.9035 4.86634 24.0449 12.8366 31.5222C20.8069 38.9995 31.617 43.2002 42.8887 43.2002C43.5517 43.2002 44.1876 42.9531 44.6564 42.5133C45.1253 42.0734 45.3887 41.4769 45.3887 40.8548V32.6461C45.3887 32.024 45.1253 31.4275 44.6564 30.9877C44.1876 30.5478 43.5517 30.3007 42.8887 30.3007Z"/></svg>`,
};

const escapeCustomButtonAttr = value =>
  String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const getCustomButtonSecondaryText = data => (data.enableSecondLine ? data.secondaryText.trim() : "");

const hasCustomButtonIcon = data => data.enableIcon && (data.icon !== "custom" || !!data.customIconSrc);

const getCustomButtonClassNames = data =>
  [
    "custom-button",
    data.enableHover && "custom-button--hover",
    data.enablePulse && "custom-button--pulse",
    data.enableFullWidth && "custom-button--full-width",
    data.enableNoWrap && "custom-button--no-wrap",
  ].filter(Boolean);

const getCustomButtonStyle = data => ({
  backgroundColor: data.bgColor,
  color: data.textColor,
  ...(data.fontFamily ? { fontFamily: data.fontFamily } : {}),
  borderRadius: `${data.borderRadius}px`,
  border: data.enableBorder ? `${data.borderWidth}px solid ${data.borderColor}` : "0 solid transparent",
  gap: `${data.iconGap}px`,
  ...(data.enableFullWidth ? {} : { width: `${data.width}px` }),
  ...(data.enable3d
    ? { boxShadow: `${data.shadowX}px ${data.shadowY}px ${data.shadowBlur}px ${data.shadowColor}` }
    : {}),
  "--cb-padding-y": `${data.paddingY}px`,
  "--cb-padding-x": `${data.paddingX}px`,
  "--cb-font-size": `${data.fontSize}px`,
  "--cb-text-length": `${Math.max(data.text.trim().length, 1)}`,
  "--cb-secondary-font-size": `${data.secondaryFontSize}px`,
  "--cb-secondary-text-length": `${Math.max(getCustomButtonSecondaryText(data).length, 1)}`,
  "--cb-line-gap": `${data.lineGap}px`,
  "--cb-icon-size": `${data.iconSize}px`,
  ...(data.enableHover ? { "--cb-hover-bg": data.hoverColor } : {}),
});

const formatCustomButtonStyle = data =>
  Object.entries(getCustomButtonStyle(data))
    .map(([name, value]) => {
      const property = name.startsWith("--") ? name : name.replace(/[A-Z]/g, char => `-${char.toLowerCase()}`);

      return `${property}: ${value}`;
    })
    .join("; ");

const getCustomButtonIconMarkup = data => {
  if (!hasCustomButtonIcon(data)) return "";

  if (data.icon === "custom") {
    const isApiAsset =
      isRelativeAssetUrl(data.customIconSrc) || (!!IMAGES_API && data.customIconSrc.startsWith(IMAGES_API));
    const src = escapeCustomButtonAttr(toAbsoluteAssetUrl(data.customIconSrc));

    return `<img class="custom-button__icon"${isApiAsset ? " data-image" : ""} src="${src}" alt="">`;
  }

  const svg = CUSTOM_BUTTON_ICON_SVG[data.icon];

  return svg
    ? `<span class="custom-button__icon" style="color: ${escapeCustomButtonAttr(data.iconColor)};">${svg}</span>`
    : "";
};

const getCustomButtonView = data => ({
  classNames: getCustomButtonClassNames(data).join(" "),
  style: escapeCustomButtonAttr(formatCustomButtonStyle(data)),
  href: escapeCustomButtonAttr(data.url || "#"),
  target: escapeCustomButtonAttr(data.openLink),
  iconMarkup: getCustomButtonIconMarkup(data),
  text: data.text,
  secondaryText: getCustomButtonSecondaryText(data),
  secondaryTextColor: escapeCustomButtonAttr(data.secondaryTextColor),
  enableAd: data.enableAd,
});

const encodeData = data => btoa(unescape(encodeURIComponent(JSON.stringify(data))));

const decodeData = encoded => {
  if (!encoded) return {};

  try {
    return JSON.parse(decodeURIComponent(escape(window.atob(encoded))));
  } catch {
    return {};
  }
};

const readCustomButtonData = (editor, shortcodeWrapper) => {
  const link = shortcodeWrapper.querySelector(".custom-button");
  const href = editor.dom.getAttrib(link, "href").trim();

  return {
    ...decodeData(shortcodeWrapper.getAttribute(DATA_ATTR)),
    url: href === "#" ? "" : href,
    openLink: editor.dom.getAttrib(link, "target"),
  };
};

const applyCustomButton = (wrapperEl, data) => {
  wrapperEl.setAttribute(DATA_ATTR, encodeData(data));
  wrapperEl.querySelector(".custom-button").outerHTML = getCustomButtonTemplate(getCustomButtonView(data));
};

const openCustomButtonDialog = (editor, { initialData, onSubmit }) => {
  let currentData = null;
  let isValid = null;

  const dialog = editor.windowManager.openUrl({
    title: DIALOG_TITLE,
    url: DIALOG_URL,
    buttons: dialogButtons,
    ...DIALOG_SIZE,
    onAction: instance => {
      if (!isValid || !currentData) {
        dialog.sendMessage({ type: CUSTOM_BUTTON_EVENT_TYPES.NEED_VALIDATION, data: true });

        return;
      }

      onSubmit(instance, currentData);
      instance.close();
    },
    onMessage: (instance, message) => {
      if (message.mceAction === CUSTOM_BUTTON_EVENT_TYPES.REQUEST_DATA) {
        dialog.sendMessage({ type: CUSTOM_BUTTON_EVENT_TYPES.SEND_DATA, data: initialData });
      }

      if (message.mceAction === CUSTOM_BUTTON_EVENT_TYPES.UPDATE_DATA) {
        currentData = message.data;
      }

      if (message.mceAction === CUSTOM_BUTTON_EVENT_TYPES.VALIDATION) {
        isValid = message.data.isValid;
      }
    },
  });
};

const customButtonConfig = (tinymce, uuid, shortcodeOnClick) => {
  openCustomButtonDialog(tinymce.activeEditor, {
    initialData: {},
    onSubmit: (instance, data) => {
      const parentElement = tinymce.activeEditor.selection.getNode().nodeName.toLowerCase();
      const wrapperEl = parseToHtml(
        getShortcodeWrapper(uuid, shortcodeOnClick, getCustomButtonTemplate(getCustomButtonView(data))),
      ).querySelector(".shortcode");

      wrapperEl.setAttribute(DATA_ATTR, encodeData(data));
      addShortcodeToEditor(wrapperEl.outerHTML, uuid, parentElement, instance);
    },
  });
};

const editCustomButtonConfig = (editor, shortcodeWrapper) => {
  openCustomButtonDialog(editor, {
    initialData: readCustomButtonData(editor, shortcodeWrapper),
    onSubmit: (instance, data) => {
      applyCustomButton(shortcodeWrapper, data);
      editor.fire("change");
    },
  });
};

export {
  customButtonConfig,
  editCustomButtonConfig,
  getCustomButtonSecondaryText,
  getCustomButtonClassNames,
  getCustomButtonStyle,
  getCustomButtonIconMarkup,
  getCustomButtonView,
};
